import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NumericInput } from "@/components/ui/numeric-input";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { formatNumber } from "@/lib/utils";

interface ReceiveWipModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transfer: any;
  onSuccess: () => void;
}

export function ReceiveWipModal({
  open,
  onOpenChange,
  transfer,
  onSuccess,
}: ReceiveWipModalProps) {
  const [receivedQty, setReceivedQty] = useState<number>(0);
  const [rejectedQty, setRejectedQty] = useState<number>(0);
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const [receiverName, setReceiverName] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (transfer) {
      setReceivedQty(transfer.quantity || 0);
      setRejectedQty(0);
      setRejectionReason("");
      setReceiverName("");
      setNotes("");
    }
  }, [transfer, open]);

  if (!transfer) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (receivedQty <= 0) {
      toast.error("Số lượng thực nhận phải lớn hơn 0");
      return;
    }

    if (rejectedQty > 0 && !rejectionReason.trim()) {
      toast.error("Vui lòng nhập lý do phế phẩm / hao hụt");
      return;
    }

    try {
      setIsSubmitting(true);
      await trpcClient.wipTransfer.receive.mutate({
        id: transfer.id,
        receivedQty,
        rejectedQty,
        rejectionReason: rejectionReason.trim() || undefined,
        receiverName: receiverName.trim() || undefined,
        notes: notes.trim() || undefined,
      });

      toast.success(`Đã tiếp nhận thành công BTP ${transfer.transferId}`);
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || "Không thể tiếp nhận bán thành phẩm");
    } finally {
      setIsSubmitting(false);
    }
  };

  const po = transfer.productionOrder || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-bold">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            Tiếp nhận Bán thành phẩm
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Summary Card */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1.5">
            <div className="flex justify-between">
              <span className="text-slate-500">Mã phiếu:</span>
              <span className="font-mono font-bold text-slate-800">{transfer.transferId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Lệnh SX:</span>
              <span className="font-mono font-semibold text-indigo-700">{po.orderId || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Sản phẩm:</span>
              <span className="font-medium text-slate-800 text-right max-w-[280px] break-words whitespace-normal">{po.productName || "—"}</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1.5">
              <span className="text-slate-500">Luân chuyển:</span>
              <span className="font-semibold text-slate-700">
                {transfer.fromStep} → <span className="text-emerald-700 font-bold">{transfer.toStep}</span>
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Số lượng bàn giao:</span>
              <span className="font-mono font-bold text-slate-900 text-sm">
                {formatNumber(transfer.quantity)} Tờ
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">
                Số lượng thực nhận (Tờ) <span className="text-rose-500">*</span>
              </Label>
              <NumericInput
                value={receivedQty}
                onChange={(val) => setReceivedQty(typeof val === "number" ? val : Number(val?.target?.value || val || 0))}
                className="font-mono font-bold text-emerald-700"
                min={1}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">
                Phế phẩm / Lỗi phát hiện (Tờ)
              </Label>
              <NumericInput
                value={rejectedQty}
                onChange={(val) => setRejectedQty(typeof val === "number" ? val : Number(val?.target?.value || val || 0))}
                className="font-mono font-semibold text-rose-600"
                min={0}
              />
            </div>
          </div>

          {rejectedQty > 0 && (
            <div className="space-y-1.5 p-2.5 bg-rose-50 border border-rose-200 rounded-md">
              <Label className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                Lý do phế phẩm / lỗi phát hiện <span className="text-rose-600">*</span>
              </Label>
              <Input
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="VD: Lem mực, xước màng, rách phôi từ trạm in..."
                className="text-xs bg-white"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Thợ máy / Người nhận</Label>
              <Input
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                placeholder="Tên thợ máy nhận ca..."
                className="text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Ghi chú nhận BTP</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ghi chú thêm nếu có..."
                className="text-xs"
              />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 font-bold"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Đang xử lý...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  Xác nhận tiếp nhận
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
